<template>
  <SectionWrapper css="secondary">
    <Tabs :value="selectedTab">
      <TabList>
        <Tab
          v-for="d in props.data"
          :key="d.table.key"
          :value="d.header.value"
          as="div"
          class="flex items-center"
          @click="onTabClick(d.header.value)"
        >
          <!-- <i :class="d.header.icon" /> -->
          <span class="whitespace-nowrap">{{ t(`labels.${d.header.label}`) }}</span>
        </Tab>
      </TabList>
      <div class="p-tabpanels">
        <div
          v-for="(d, idx) in props.data"
          :key="idx"
          :class="['p-tabpanel', `${idx}` === `${selectedTab}` ? 'tab-open' : 'tab-closed']"
        >
          <DataTable
            :isSubcomponent="true"
            :data-id="d.table.id || ''"
            :data-key="d.table.key || ''"
            :data="`${idx}` === `${selectedTab}` ? paginatedTabData : []"
            :columns="d.table.columns || []"
            :reorderableRows="d.table.reorderableRows"
            :showPrimaryToolbar="d.showPrimaryToolbar"
            :onRowClick="d.onRowClick"
            :onRowDblClick="d.onRowDblClick"
            :onCreate="d.onCreate"
            :showDelete="d.showDelete"
            :prefilterTabs="d.table.prefilterTabs"
            :applySort="d.table.applySort"
            :editMode="!!d.handleCellEdit"
            :onCellEdit="d.handleCellEdit"
            @delete="onDelete"
          >
            <template
              v-for="slot in filteredSlots(d.table.key)"
              #[getSlotName(slot,d.table.key)]="slotProps"
            >
              <slot :name="slot" v-bind="slotProps"></slot>
            </template>
          </DataTable>
        </div>
      </div>
      <Toolbar v-if="shouldShowPaginator" class="mt-2 p-0">
        <template #start>
          <Paginator
            :first="currentPage * pageSize"
            :rows="pageSize"
            :pageLinkSize="3"
            :totalRecords="selectedTabData?.data.length || 0"
            :rowsPerPageOptions="[10, 20, 50]"
            @page="handlePaginatorChange"
        /></template>
      </Toolbar>
    </Tabs>
  </SectionWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useSlots, watch } from 'vue';
import Paginator from 'primevue/paginator';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import DataTable from '@/components/datatable/DataTable.vue';
import type {
  CellEditEvent,
  ColumnConfig,
  DataTableProps,
} from '@/components/datatable/dataTableTypes';
import { useSelectedTabsStore } from '@/stores/selectedSubcomponentTabs';
import type { PrefilterTab } from '@/utils/prefilter';

const route = useRoute();
const { t } = useI18n();
const selectedTabsStore = useSelectedTabsStore();

const props = defineProps<DetailRelationProps>();

const selectedTab = ref<string>(props.initialTab || '0');
const selectedTabData = ref<TableItem | undefined>();

// Pagination state
const currentPage = ref<number>(0);
const pageSize = ref<number>(10);

// Track pagination state per tab (key: tab key, value: { page, size })
const tabPaginationState = ref<Map<string, { page: number; size: number }>>(new Map());

const slots = useSlots();

const filteredSlots = (dataKey: string) => {
  return Object.keys(slots).filter((slot) => slot.startsWith(`${dataKey}-`));
};

const getSlotName = (slot: string, dataKey: string) => {
  return slot.replace(`${dataKey}-`, '');
};

const getData = () => {
  return props.data.find((d) => d.header.value === selectedTab.value)?.table;
};

// Compute paginated data for the current selected tab
const paginatedTabData = computed(() => {
  if (!selectedTabData.value?.data) return [];

  const data = selectedTabData.value.data;
  const start = currentPage.value * pageSize.value;
  const end = start + pageSize.value;

  return data.slice(start, end);
});

// Check if paginator should be shown for current tab
const shouldShowPaginator = computed(() => {
  return selectedTabData.value && selectedTabData.value.data.length > 10;
});

const onTabClick = (value: string) => {
  // Save current tab's pagination state before switching
  const currentKey = props.data.find((d) => d.header.value === selectedTab.value)?.table.key;
  if (currentKey) {
    tabPaginationState.value.set(currentKey, {
      page: currentPage.value,
      size: pageSize.value,
    });
  }

  selectedTab.value = value;
  selectedTabData.value = getData();

  const key = props.data.find((d) => d.header.value === value)?.table.key;
  if (key) {
    const dataKey = route.params.mainComponentID as string;
    selectedTabsStore.setSelectedTab(dataKey, key);

    // Restore pagination state for the new tab, or reset to page 0
    const savedState = tabPaginationState.value.get(key);
    if (savedState) {
      currentPage.value = savedState.page;
      pageSize.value = savedState.size;
    } else {
      currentPage.value = 0;
      pageSize.value = 10;
    }
  }
};

const selectTabByKey = (key: string) => {
  const tab = props.data.find((d) => d.table.key === key);
  if (tab) {
    selectedTab.value = tab.header.value;
    selectedTabData.value = getData();
  }
};

watch([selectedTab, () => props.data], () => {
  selectedTabData.value = getData();
});

const onDelete = (ids: string[]) => {
  if (selectedTabData.value?.key) {
    props.onDelete(ids, selectedTabData.value?.key);
  }
};

const setSelectedTabByPathOrStore = () => {
  const dataKey = route.params.mainComponentID as string;

  // 1. Prio: Tab from URL
  if (route.params.subComponent) {
    const tabKeyFromRoute = route.params.subComponent as string;
    const tabExists = props.data.some((d) => d.table.key === tabKeyFromRoute);

    if (tabExists) {
      selectTabByKey(tabKeyFromRoute);
      return;
    }
  }

  // 2. Prio: Tab from Store
  const savedTabKey = selectedTabsStore.getSelectedTab(dataKey);
  if (savedTabKey) {
    const tabExists = props.data.some((d) => d.table.key === savedTabKey);

    if (tabExists) {
      selectTabByKey(savedTabKey);
      return;
    }
  }

  // 3. Prio: Standard value
  if (props.initialTab) {
    const initialTabExists = props.data.some((d) => d.header.value === props.initialTab);

    if (initialTabExists) {
      selectedTab.value = props.initialTab;
      selectedTabData.value = getData();
      return;
    }
  }

  // Fallback: Choose first tab from list
  if (props.data.length > 0) {
    selectedTab.value = props.data[0]?.header.value ?? '';
    selectedTabData.value = getData();
  }
};

function handlePaginatorChange(event: { page: number; rows: number }) {
  currentPage.value = event.page;
  updatePageSize(event.rows);
}

function updatePageSize(newPageSize: number) {
  if (newPageSize === pageSize.value) return;
  pageSize.value = newPageSize;
  currentPage.value = 0; // Reset to first page when size changes
}

onMounted(() => {
  setSelectedTabByPathOrStore();
});
</script>

<script lang="ts">
export interface DetailRelationProps {
  data: DetailRelationItem[];
  initialTab?: string;
  onDelete: (ids: string[], key: string) => void;
}

export interface DetailRelationItem {
  header: HeaderItem;
  table: TableItem;
  reorderableRows: boolean;
  showPrimaryToolbar: boolean;
  showDelete?: boolean;
  onCreate?: () => void;
  onRowClick?: () => void;
  onRowDblClick?: () => void;
  handleCellEdit?: (payload: CellEditEvent) => void;
}

export interface HeaderItem {
  label: string;
  icon: string;
  value: string;
}

export interface TableItem {
  id?: string;
  key: string;
  data: any[];
  columns: ColumnConfig[];
  reorderableRows: boolean;
  prefilterTabs?: PrefilterTab[];
  applySort?: DataTableProps['applySort'];
}
</script>

<style scoped lang="css">
@reference "tailwindcss";
@reference "tailwindcss/utilities";

@layer app {
  .p-tabs {
    background-color: transparent;
    overflow-x: hidden;
  }

  .p-tab {
    @apply px-4 py-2;
  }

  /* :deep() */
  .p-tablist-tab-list {
    background: transparent;
  }

  .p-tabpanels {
    background-color: transparent;
  }

  /* alternative way to hide tabs */
  .tab-closed {
    position: relative;
    overflow: hidden;
    height: 0;
  }
}
</style>

<style lang="css">
@layer app {
  .secondary .primaryToolbar {
    background-color: transparent;
    border: none;
  }
}
</style>
